import React from 'react';
import clsx from 'clsx';
import * as BizCharts from 'bizcharts';
import '../charts/chartTheme';
import { withNiceScale } from '../charts/chartUtils';
import {
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const AXIS_X_LINE_COLOR = '#E8E8E8';
const DEFAULT_HEIGHT = 360;
const DEFAULT_BAR_SIZE = 32;
/**
 * Comparison mode renders two bars per month side-by-side. Narrower than single-bar mode so
 * the current + previous columns leave a visible gap rather than touching shoulder-to-shoulder.
 */
const DEFAULT_BAR_SIZE_COMPARISON = 18;
/** Target intra-pair gap between the current and previous columns. */
const COMPARISON_INTRA_GAP_PX = 8;
/**
 * The exact intra-pair gap depends on the BizCharts/G2 build, but in this version `marginRatio`
 * is multiplied against the bar size rather than the dodge slot, so passing the literal pixel
 * ratio sometimes collapses to zero. Use a generous value clamped to ~50% of the bar so the
 * gap is always visible (≈9px at the 18px comparison bar width).
 */
const COMPARISON_DODGE_MARGIN_RATIO = Math.max(
	0.5,
	COMPARISON_INTRA_GAP_PX / DEFAULT_BAR_SIZE_COMPARISON
);

export type ColumnLineComboChartProps = {
	data: any[];
	xField?: string;
	/** Field for the column (interval) geom — e.g., totalWOs. */
	columnYField: string;
	/** Field for the secondary line geom — e.g., averageWOCost. */
	lineYField: string;
	hasComparison: boolean;
	currentColor: string;
	previousColor: string;
	/** Display alias for each scale field (BizCharts cols). Used for axis aliases. */
	cols?: any;
	axisXTitle?: string;
	columnAxisYTitle?: string;
	lineAxisYTitle?: string;
	formatColumnYAxisLabel?: (val: any) => string;
	formatLineYAxisLabel?: (val: any) => string;
	/** Custom comparison-mode tooltip builder. */
	renderComparisonTooltip?: (title: string, items: any[]) => string;
	/** BizCharts tooltip itemTpl used in single mode. */
	singleTooltipItemTemplate?: string;
	height?: number;
	barSize?: number;
};

const SINGLE_ITEM_TPL = '<li data-index={index} class="m-0 list-none p-0">{value}</li>';

const CROSSHAIRS_CONFIG = {
	type: 'x',
	line: {
		style: {
			stroke: 'rgba(85, 85, 85, 0.8)',
			lineWidth: 1,
			lineDash: [4, 4],
		},
	},
};

const ColumnLineComboChart = ({
	data,
	xField = 'monthLabel',
	columnYField,
	lineYField,
	hasComparison,
	currentColor,
	previousColor,
	cols,
	axisXTitle = 'Month',
	columnAxisYTitle,
	lineAxisYTitle,
	formatColumnYAxisLabel,
	formatLineYAxisLabel,
	renderComparisonTooltip,
	singleTooltipItemTemplate,
	height = DEFAULT_HEIGHT,
	barSize = DEFAULT_BAR_SIZE,
}: ColumnLineComboChartProps) => {
	const seriesColor = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? previousColor : currentColor;
	const seriesLineDash = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? { lineDash: [4, 4] } : {};

	// Skip the secondary line when every value is 0 / nullish; otherwise the line geom paints a
	// flat blue stroke along the x-axis (visible behind the columns in the user's reports) and
	// implies cost data that doesn't exist.
	const hasLineData = (data || []).some((row) => {
		const value = row && row[lineYField];
		return typeof value === 'number' && value > 0;
	});

	const scale = withNiceScale({
		...(cols || {}),
		[columnYField]: {
			...(cols && cols[columnYField] ? cols[columnYField] : {}),
			min: 0,
			nice: true,
		},
		[lineYField]: {
			...(cols && cols[lineYField] ? cols[lineYField] : {}),
			min: 0,
			nice: true,
		},
	});

	const g2ChartScopeClass = hasComparison
		? 'analytics-g2-chart--comparison'
		: 'analytics-g2-chart--single';

	const singleColumnTooltip: [string, (...args: any[]) => { name: string; value: string }] = [
		`${xField}*${columnYField}`,
		(monthLabel: any, columnValue: any) => ({
			name: (cols && cols[columnYField] && cols[columnYField].alias) || columnYField,
			value: formatColumnYAxisLabel ? formatColumnYAxisLabel(columnValue) : String(columnValue),
		}),
	];

	const singleLineTooltip: [string, (...args: any[]) => { name: string; value: string }] = [
		`${xField}*${lineYField}`,
		(monthLabel: any, lineValue: any) => ({
			name: (cols && cols[lineYField] && cols[lineYField].alias) || lineYField,
			value: formatLineYAxisLabel ? formatLineYAxisLabel(lineValue) : String(lineValue),
		}),
	];

	return (
		<div className={clsx('analytics-g2-chart w-full min-w-0', g2ChartScopeClass)}>
			<Chart
				height={height}
				data={data}
				autoFit={true}
				forceFit={true}
				theme="openwrench"
				scale={scale}
			>
				<Axis
					name={xField}
					title={{
						text: axisXTitle,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						// In comparison mode the per-bar rotated labels extend further below the axis
						// than a flat label would, so the title needs extra clearance to avoid sitting
						// right on top of them. Single mode uses the normal 40px offset.
						offset: hasComparison ? 60 : 40,
					}}
					line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
					tickLine={null}
					// In comparison mode we draw per-bar labels (current + previous month under each
					// dodged bar) via the interval Geom's `label` config, so the regular shared
					// X-axis label is hidden to avoid overlap. Matches the lifecycle SpendBarChart.
					label={
						hasComparison
							? null
							: {
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
							  }
					}
				/>
				<Axis
					name={columnYField}
					title={
						columnAxisYTitle
							? {
									text: columnAxisYTitle,
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
									offset: 60,
							  }
							: null
					}
					label={{
						formatter: formatColumnYAxisLabel,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					}}
					line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
					tickLine={null}
					grid={{
						line: {
							style: {
								stroke: AXIS_Y_LINE_COLOR,
								lineDash: [4, 4],
							},
						},
					}}
				/>
				<Axis
					name={lineYField}
					title={
						lineAxisYTitle
							? {
									text: lineAxisYTitle,
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
									offset: 60,
							  }
							: null
					}
					label={{
						formatter: formatLineYAxisLabel,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					}}
					line={null}
					tickLine={null}
					grid={null}
					position="right"
				/>
				<Legend visible={false} />
				{hasComparison ? (
					<Tooltip
						showTitle={false}
						shared={true}
						showCrosshairs={true}
						crosshairs={CROSSHAIRS_CONFIG}
						customContent={renderComparisonTooltip}
					/>
				) : (
					<Tooltip
						showTitle={false}
						showCrosshairs={true}
						crosshairs={CROSSHAIRS_CONFIG}
						itemTpl={singleTooltipItemTemplate || SINGLE_ITEM_TPL}
					/>
				)}
				<Geom
					key={hasComparison ? 'col-cmp' : 'col-single'}
					type="interval"
					// Narrower bars in comparison mode so the dodge gap between current + previous is
					// actually visible. G2 honors `size` even when `dodge` is applied — without this
					// the two columns inside one month band sit shoulder-to-shoulder.
					size={hasComparison ? DEFAULT_BAR_SIZE_COMPARISON : barSize}
					position={`${xField}*${columnYField}`}
					color={[COMPARISON_SERIES_FIELD, seriesColor]}
					adjust={
						hasComparison
							? [
									{
										type: 'dodge',
										marginRatio: COMPARISON_DODGE_MARGIN_RATIO,
										// Pin the dodge group to a fraction of the band so each pair of bars
										// has slack on both sides of the slot — without this, the dodge slot
										// fills the entire band and the marginRatio gap visually disappears.
										dodgeRatio: 0.5,
									},
							  ]
							: undefined
					}
					tooltip={hasComparison ? undefined : singleColumnTooltip}
					// Per-bar label in comparison mode — current bar shows its monthLabel, previous
					// bar shows its real previousMonthLabel (e.g. "Dec 2024" under the orange bar
					// next to "Dec 2025" under the blue bar). Matches lifecycle SpendBarChart's
					// alternating x-axis labels.
					label={
						hasComparison
							? [
									xField,
									{
										position: 'bottom',
										offset: -10,
										autoRotate: false,
										rotate: -Math.PI / 4,
										content: (rowData: any) =>
											rowData[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_PREVIOUS
												? rowData.previousMonthLabel || ''
												: rowData[xField] || '',
										style: {
											fontSize: 12,
											fill: 'rgba(0,0,0,0.45)',
											textAlign: 'end',
											textBaseline: 'middle',
										},
									},
							  ]
							: undefined
					}
				/>
				{hasLineData ? (
					<Geom
						key={hasComparison ? 'line-cmp' : 'line-single'}
						type="line"
						position={`${xField}*${lineYField}`}
						color={[COMPARISON_SERIES_FIELD, seriesColor]}
						size={2}
						style={[COMPARISON_SERIES_FIELD, seriesLineDash]}
						tooltip={hasComparison ? undefined : singleLineTooltip}
					/>
				) : null}
			</Chart>
		</div>
	);
};

export default ColumnLineComboChart;
